import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { AgChartOptions } from 'ag-charts-community'
import { CellValueChangedEvent, GetRowIdParams, RowClickedEvent } from 'ag-grid-enterprise'
import { Plus } from 'lucide-react'

import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Funds, Shareclasses } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { AddBucketModal } from '@/components/dialogs/AddBucketModal'
import { AddFundModal } from '@/components/dialogs/AddFundModal'
import { AddShareclassModal } from '@/components/dialogs/AddShareclassModal'
import { ADMIN } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { bucketsColDefs, createFundsColDefs, shareclassColDefs } from '@/pages/admin/funds/col-defs'
import { useUpdateFundMutation } from '@/react-query/mutations/funds'
import { useFunds } from '@/react-query/queries/funds/funds'

export interface AdminFundsPageProps {}

export const AdminFundsPage: React.FC<AdminFundsPageProps> = ({}) => {
  const [addingFund, setAddingFund] = useState(false)
  const [addingBucket, setAddingBucket] = useState(false)
  const [addingShareclass, setAddingShareclass] = useState(false)

  const api = useApi()

  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_ADMIN_FUNDS],
  }))

  const [fund_id, setFundId] = useState<string | undefined>()

  const [instrument_id, setInstrumentId] = useState<string | undefined>()

  const fundsColDefs = useMemo(() => {
    return createFundsColDefs(hasWriteAccess)
  }, [hasWriteAccess])

  const { data: fundsData, query: funds } = useFunds(true)

  const queries = useQueries({
    queries: [
      {
        queryKey: ['admin', 'funds', 'buckets'],
        queryFn: () => api.buckets.getAll(),
      },
      {
        queryKey: ['admin', 'funds', 'shareclasses', { fund_id }],
        queryFn: () => api.shareclasses.get({ fund_id }),
      },
      {
        queryKey: ['admin', 'funds', 'ocf', { instrument_id }],
        queryFn: () => api.funds.getOCFChart({ instrument_id: instrument_id! }),
        enabled: !!instrument_id,
      },
    ],
  })

  const [buckets, shareclasses, ocf] = queries

  const [bucketsData, shareclassData, ocfData] = useMemo(() => {
    return [
      buckets.data ?? [],
      shareclasses.data ?? [],
      (ocf.data ?? []).map((data) => ({
        ...data,
        date: data.date,
      })),
    ]
  }, [buckets.data, shareclasses.data, ocf.data])

  const selectedFundName = useMemo(() => {
    return fund_id
      ? fundsData.find((fund) => fund.fund_id.toString() === fund_id)?.fund_name
      : undefined
  }, [fund_id])

  const selectedISIN = useMemo(() => {
    return instrument_id
      ? shareclassData.find((shareclass) => shareclass.instrument_id.toString() === instrument_id)
          ?.isin
      : undefined
  }, [instrument_id])

  const chartOptions: AgChartOptions<(typeof ocfData)[number]> = {
    data: ocfData,
    zoom: {
      enabled: true,
    },
    height: 400,
    legend: {
      item: {
        marker: {
          shape: 'square',
          strokeWidth: 0,
        },
      },
    },
    title: {
      text: 'OCF Over Time',
      fontSize: 18,
      spacing: 25,
    },
    series: [
      {
        type: 'line',
        xKey: 'date',
        yKey: 'addl_bps',
        yName: 'Addl Fees',
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'mgnt_bps',
        yName: 'Mgt Fees',
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'totl_bps',
        yName: 'Total Fees',
      },
    ],
  }

  const updateFundMutation = useUpdateFundMutation()

  return (
    <Page metadata={{ title: 'Funds' }} permissions={ADMIN.FUNDS.permissions}>
      <CanAccess permissions={[PERMISSIONS.WRITE_ADMIN_FUNDS]}>
        <AddFundModal open={addingFund} onOpenChange={setAddingFund} />
        <AddBucketModal open={addingBucket} onOpenChange={setAddingBucket} />
        <AddShareclassModal open={addingShareclass} onOpenChange={setAddingShareclass} />
        <HeaderConfigBar>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant={'outline'}>
                <Plus />
                Add new
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={'start'}>
              <DropdownMenuItem onClick={() => setAddingFund(true)}>
                <Plus /> Fund
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddingShareclass(true)}>
                <Plus /> Shareclass
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddingBucket(true)}>
                <Plus /> Bucket
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </HeaderConfigBar>
      </CanAccess>

      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel>
          <ResizablePanelGroup direction={'horizontal'}>
            <ResizablePanel>
              <AgGridWidget
                query={funds}
                widget={{ exposed: true, className: 'h-full' }}
                widgetHeader={{ title: 'Funds Data' }}
                grid={{
                  rowData: fundsData,
                  columnDefs: fundsColDefs,
                  domLayout: 'normal',
                  getRowId: (params: GetRowIdParams<Funds.Fund>) => params.data.fund_id.toString(),
                  onRowClicked: (params: RowClickedEvent<Funds.Fund>) => {
                    !params.node.group && setFundId(params.data?.fund_id.toString())
                  },
                  onCellValueChanged: (params: CellValueChangedEvent<Funds.Fund>) => {
                    return updateFundMutation.mutateAsync({
                      rowData: params.data,
                      oldValue: params.oldValue,
                      newValue: params.newValue,
                      updatedField: params.colDef.field!,
                    })
                  },
                }}
              />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={30}>
              <AgGridWidget
                query={buckets}
                widget={{ exposed: true, className: 'h-full' }}
                widgetHeader={{ title: 'Buckets Data' }}
                grid={{
                  rowData: bucketsData,
                  columnDefs: bucketsColDefs,
                  domLayout: 'normal',
                  // onCellValueChanged: (
                  //   params: CellValueChangedEvent<Buckets.GetBucketsData[number]>,
                  // ) => {
                  //   return updateBucketMutation.mutateAsync({
                  //     rowData: params.data,
                  //     oldValue: params.oldValue,
                  //     newValue: params.newValue,
                  //     updatedField: params.colDef.field!,
                  //   })
                  // },
                  // getContextMenuItems: bucketContextMenuItems,
                }}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <ResizablePanelGroup direction={'horizontal'}>
            <ResizablePanel>
              <AgGridWidget
                query={shareclasses}
                widget={{ exposed: true, className: 'h-full' }}
                widgetHeader={{
                  title: `Shareclass Data${selectedFundName ? ` - ${selectedFundName}` : ''}`,
                }}
                grid={{
                  rowData: shareclassData,
                  columnDefs: shareclassColDefs,
                  domLayout: 'normal',
                  onRowClicked: (
                    params: RowClickedEvent<Shareclasses.GetShareclassesData[number]>,
                  ) => {
                    !params.node.group && setInstrumentId(params.data?.instrument_id!.toString())
                  },
                }}
              />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={30}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  query={ocf}
                  title={`OCF Over Time Chart${selectedISIN ? ` - ${selectedISIN}` : ''}`}
                />
                <WidgetContent loading={ocf.isLoading}>
                  <CustomAgChart options={chartOptions} className={'h-full'} />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
